import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IPropertyChainData, useChainData } from "../../components/chain-data-provider";
import { IPropertyLoyaltyBrand, useLoyaltyBrand } from "../../components/loyalty-brand-provider";
import { Container } from "./styles";
import { usePropertyLocales } from "../../components/property-locale-provider";
import { Grid, Table, TableHeaderRow } from "@devexpress/dx-react-grid-bootstrap4";
import styled from "styled-components";
import { format } from "date-fns";
import { Button } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { useGrpcClient } from "../../lib/grpc";
import { LoyaltyTierAPIPromiseClient } from "../../gen/witbooking/backoffice/loyalty/tier/v1/loyalty_tier_api_grpc_web_pb";
import {
  GetLoyaltyTiersByBrandRequest,
  LoyaltyConstantTier,
} from "../../gen/witbooking/backoffice/loyalty/tier/v1/loyalty_tier_api_pb";
import Selector from "../../components/selector";
import SwitchComponent from "../witadmin/CRUD/Common/SwitchGroup";
import { usePropertyConfiguration } from "../../components/property-configuration-provider";

export enum AccountType {
  agency = "agency",
  company = "company",
  customer = "customer",
}
export interface LoyaltyLink {
  id: number;
  propertyTicker: string;
  locale: string;
  accountType: AccountType;
  url: string;
}

export interface BuildLinksProps {
  chainData: IPropertyChainData;
  loyaltyBrand: IPropertyLoyaltyBrand;
  allowedLocales: string[];
  gtmCodes: string;
  propertyTicker: string;
  showFormTypeMenu: boolean;
  constantTiers: LoyaltyConstantTier.AsObject[];
  params: Record<string, string>;
  agencyTier?: LoyaltyConstantTier.AsObject;
  companyTier?: LoyaltyConstantTier.AsObject;
}

const GridContainer = styled.div`
  overflow: auto;
  height: calc(100vh - 300px);
  width: 100%;
`;

export interface Column {
  name: string;
  title: string;
}

function getEncodedQueryParams(params: Record<string, string | number>) {
  const uri = Object.entries(params)
    .filter(it => !!it[1])
    .map(([key, value]) => `${key}=${value}`)
    .join("&");
  return encodeURIComponent(`?${uri}`);
}

function buildLinks({
  chainData,
  loyaltyBrand,
  allowedLocales,
  gtmCodes,
  propertyTicker,
  agencyTier,
  companyTier,
  showFormTypeMenu,
  params,
}: BuildLinksProps): LoyaltyLink[] {
  // https://{{dominio_loyalty}}/auth/realms/{{brand}}/protocol/openid-connect/auth
  // ?form_type={{form_type}}
  // &response_type=code
  // &client_id=engine
  // &scope=openid
  // &redirect_uri=https://{{mainDomain}}/api/loyalty/{{brand}}/{{ticker_motor}}/session
  // &property_ticker={{ticker_motor}}
  // &ui_locales={{idioma}}
  // &brandTicker={{brand}}
  // &gtmCodes={{codigos_UA_separados_por_coma}}
  const authUrl = `https://${loyaltyBrand.authorizationServiceDomain}/auth`;
  const propertyTickers = [propertyTicker, ...chainData.children];
  const allowedAccountType = [
    AccountType.customer,
    agencyTier ? AccountType.agency : undefined,
    companyTier ? AccountType.company : undefined,
  ].filter((it): it is AccountType => Boolean(it));
  const loyaltyLinks: LoyaltyLink[] = propertyTickers
    .flatMap(propertyTicker => allowedLocales.map(locale => ({ propertyTicker, locale })))
    .flatMap(obj =>
      Object.values(AccountType)
        .filter(at => allowedAccountType.includes(at))
        .map(accountType => ({ ...obj, accountType, url: "" }))
    )
    .map((it, id) => ({ id, ...it }));
  return loyaltyLinks.map(link => {
    const encodedQueryParams = getEncodedQueryParams({
      language: link.locale,
      locale: link.locale,
      ...params,
    });
    const baseRedirectUri = `https://${loyaltyBrand.mainDomain}/api/loyalty/${loyaltyBrand.ticker}/${link.propertyTicker}/session${encodedQueryParams}`;
    const agencyTierPart =
      (link.accountType !== AccountType.customer || showFormTypeMenu) && agencyTier
        ? `&tierAgencyId=${agencyTier.externalSystemId}`
        : "";
    const companyTierPart =
      (link.accountType !== AccountType.customer || showFormTypeMenu) && companyTier
        ? `&tierCompanyId=${companyTier.externalSystemId}`
        : "";
    const url =
      `${authUrl}/realms/${loyaltyBrand.ticker}/protocol/openid-connect/auth` +
      `?response_type=code` +
      (link.accountType !== AccountType.customer ? `&form_type=${link.accountType}` : "") +
      `&client_id=engine` +
      `&scope=openid` +
      `&redirect_uri=${baseRedirectUri}` +
      `&property_ticker=${propertyTicker}` +
      `&ui_locales=${link.locale}` +
      `&brandTicker=${loyaltyBrand.ticker}` +
      `&gtmCodes=${gtmCodes}` +
      agencyTierPart +
      companyTierPart;

    return { ...link, url };
  });
}
const columns: Column[] = [
  {
    name: "id",
    title: "id",
  },
  {
    name: "propertyTicker",
    title: "propertyTicker",
  },
  {
    name: "locale",
    title: "locale",
  },
  {
    name: "accountType",
    title: "accountType",
  },
  {
    name: "url",
    title: "url",
  },
];

const columnWidths = [
  { columnName: "id", width: "10%" },
  { columnName: "propertyTicker", width: "10%" },
  { columnName: "locale", width: "10%" },
  { columnName: "accountType", width: "10%" },
  { columnName: "url", width: "60%" },
];

const getRowId = (row: LoyaltyLink) => row.id;

export interface GetParamsBuilderProps {
  onChange: ({ params }: { params: Record<string, string> }) => void;
  params: Record<string, string>;
}
function GetParamsBuilder({ onChange, params }: GetParamsBuilderProps) {
  const [paramNumber, setParamNumber] = useState<number>(1);
  const [paramNumberMap, setParamNumberMap] = useState<
    Record<string, { key: string; val: string }>
  >(Object.fromEntries(Object.entries(params).map(([key, val], index) => [index, { key, val }])));
  const handleAddEmptyParam = useCallback(() => setParamNumber(prevState => prevState + 1), []);
  const handleRemoveLastParam = useCallback(() => {
    setParamNumber(prevState => {
      const newLength = prevState - 1;
      setParamNumberMap(mapPrevState => {
        const cloned = { ...mapPrevState };
        delete cloned[newLength];
        return cloned;
      });
      return newLength;
    });
  }, []);
  const handleSetParamKey = useCallback(
    (idx, keyName) =>
      setParamNumberMap(prevState => ({
        ...prevState,
        [idx]: {
          key: keyName,
          val: prevState[idx]?.val ?? "",
        },
      })),
    []
  );
  const handleSetParamVal = useCallback(
    (idx, value) =>
      setParamNumberMap(prevState => ({
        ...prevState,
        [idx]: {
          key: prevState[idx]?.key ?? "",
          val: value,
        },
      })),
    []
  );

  const handleChange = useCallback(() => {
    const newParams = Object.fromEntries(
      Object.values(paramNumberMap).map(({ key, val }) => [key, val])
    );
    onChange({ params: newParams });
  }, [onChange, paramNumberMap]);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "32px" , marginTop: "16px" }}>
      {[...Array(paramNumber)].map((_value, idx) => {
        return (
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              style={{
                width: "280px",
                border: "1px solid hsl(0, 0%, 80%)",
                borderRadius: "4px",
                padding: "4px",
                paddingLeft: "10px",
              }}
              onChange={event => handleSetParamKey(idx, event.target.value)}
              value={paramNumberMap[idx]?.key ?? ""}
              placeholder={`param-key-${idx}`}
            />
            <input
              style={{
                width: "280px",
                border: "1px solid hsl(0, 0%, 80%)",
                borderRadius: "4px",
                padding: "4px",
                paddingLeft: "10px",
              }}
              onChange={event => handleSetParamVal(idx, event.target.value)}
              value={paramNumberMap[idx]?.val ?? ""}
              placeholder={`param-value-${idx}`}
            />
            {idx > 0 && idx === paramNumber - 1 ? (
              <Button variant="primary" onClick={handleRemoveLastParam}>
                Remove
              </Button>
            ) : null}
          </div>
        );
      })}
      <div style={{ display: "flex", gap: "8px" }}>
        <Button
          variant="primary"
          style={{ width: 150, marginTop: 16 }}
          onClick={handleAddEmptyParam}>
          Add
        </Button>
        <Button variant="primary" style={{ width: 150, marginTop: 16 }} onClick={handleChange}>
          Apply
        </Button>
      </div>
    </div>
  );
}

function LoyaltyLinksGenerator({
  chainData,
  allowedLocales,
  loyaltyBrand,
  propertyTicker,
  constantTiers,
  gtmCodes: configuredGTMCodes,
}: Omit<BuildLinksProps, "params" | "showFormTypeMenu">) {
  const [params, setParams] = useState<Record<string, string>>({});
  const [gtmCodes, setGtmCodes] = useState<string>(configuredGTMCodes);
  const [agencyTier, setAgencyTier] = useState<LoyaltyConstantTier.AsObject | undefined>(undefined);
  const [companyTier, setCompanyTier] = useState<LoyaltyConstantTier.AsObject | undefined>(
    undefined
  );
  const [showFormTypeMenu, setShowFormTypeMenu] = useState<boolean>(false);
  const links = useMemo(
    () =>
      buildLinks({
        chainData,
        allowedLocales,
        loyaltyBrand,
        gtmCodes,
        propertyTicker,
        constantTiers,
        params,
        agencyTier,
        companyTier,
        showFormTypeMenu,
      }),
    [
      agencyTier,
      allowedLocales,
      gtmCodes,
      chainData,
      companyTier,
      constantTiers,
      loyaltyBrand,
      propertyTicker,
      showFormTypeMenu,
      params,
    ]
  );

  const handleSetGtmCodes = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setGtmCodes(event.target.value),
    []
  );
  const handleSelectAgencyTier = useCallback(
    option => {
      setAgencyTier(constantTiers.find(it => it.externalSystemId === option.value));
    },
    [constantTiers]
  );
  const handleSelectCompanyTier = useCallback(
    option => {
      setCompanyTier(constantTiers.find(it => it.externalSystemId === option.value));
    },
    [constantTiers]
  );
  const handleShowFormTypeMenu = useCallback(
    ({ event }: { event: React.ChangeEvent<HTMLInputElement> }) =>
      setShowFormTypeMenu(event.target.checked),
    []
  );
  const handleGetParamsChange = useCallback(({ params }: { params: Record<string, string> }) => {
    setParams(params);
  }, []);
  const options = constantTiers?.map(it => ({ value: it.externalSystemId, label: it.ticker }));
  return (
    <Container>
      <h2 style={{ fontWeight: "bold" }}>Plantilla</h2>
      <pre style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1rem" }}>
        {`
          https://{{dominio_loyalty}}/auth/realms/{{brand}}/protocol/openid-connect/auth
          ?form_type={{form_type}}
          &response_type=code
          &client_id=engine
          &scope=openid
          &redirect_uri=https://{{mainDomain}}/api/loyalty/{{brand}}/{{ticker_motor}}/session
          &property_ticker={{ticker_motor}}
          &ui_locales={{idioma}}
          &brandTicker={{brand}}
          &gtmCodes={{codigos_UA_separados_por_coma}}
        `}
      </pre>
      {constantTiers.length ? (
        <>
          <h4 style={{ fontWeight: "bold" }}>
            En caso de agencia/empresa a la plantilla anterior deben añadirse los tiers por defecto
            que se asignarán, ya que no hay tier base. El añadir estos valores a la url de
            particular hace que aparezcan los menus de agencia / empresa.
          </h4>
          <pre style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1rem" }}>
            {`
          &tierCompanyId={{external_constant_tier_id}}
          &tierAgencyId={{external_constant_tier_id}}
            `}
          </pre>
        </>
      ) : null}

      <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "-15px" }}>
          <SwitchComponent
            value={showFormTypeMenu}
            onChange={handleShowFormTypeMenu}
            field={{
              className: "witadmin-bs3",
            }}
            caller=""
            entity={{}}
          />
          Show form type menu for customers
        </div>
        <div style={{ width: "180px" }}>
          <Selector
            options={options}
            value={
              agencyTier ? options.find(it => it.value === agencyTier.externalSystemId) : undefined
            }
            onChange={handleSelectAgencyTier}
            placeholder={"Agency tier"}
          />
        </div>
        <div style={{ width: 180 }}>
          <Selector
            options={options}
            value={
              companyTier
                ? options.find(it => it.value === companyTier.externalSystemId)
                : undefined
            }
            onChange={handleSelectCompanyTier}
            placeholder={"Company tier"}
          />
        </div>
        <input
          style={{
            width: "280px",
            border: "1px solid hsl(0, 0%, 80%)",
            borderRadius: "4px",
            padding: "4px",
            paddingLeft: "10px",
          }}
          onChange={handleSetGtmCodes}
          value={gtmCodes}
          placeholder="GTM codes separated by comma"
        />

        <CSVLink
          style={{ marginLeft: "10px", marginRight: "18px" }}
          data={links}
          filename={`${loyaltyBrand.ticker}_links_export_${format(new Date(), "yyyyMMdd")}.csv`}>
          <Button variant="primary">Export</Button>
        </CSVLink>
      </div>
      <div>
        <h4 style={{ fontWeight: "bold" }}>
          Parámetros GET Adicionales que deben ser pasados al motor luego de un registro / login
          exitoso
        </h4>
        <GetParamsBuilder params={params} onChange={handleGetParamsChange} />
      </div>
      <GridContainer id="GridContainer">
        <Grid rows={links} columns={columns} getRowId={getRowId}>
          <Table columnExtensions={columnWidths} />
          <TableHeaderRow />
        </Grid>
      </GridContainer>
    </Container>
  );
}
function LoyaltyLinksGeneratorWrapper({ propertyTicker }: { propertyTicker: string }) {
  const allowedLocales: string[] = usePropertyLocales();
  const propertyConfiguration = usePropertyConfiguration();
  const chainData: IPropertyChainData | undefined | null = useChainData();
  const loyaltyBrand: IPropertyLoyaltyBrand | undefined | null = useLoyaltyBrand();
  const client = useGrpcClient(LoyaltyTierAPIPromiseClient);
  const [constantTiers, setConstantTiers] = useState<LoyaltyConstantTier.AsObject[] | undefined>(
    undefined
  );
  const gtmCodes =
    (propertyConfiguration?.gtmCodes as string)
      ?.split(",")
      .map(it => it.trim())
      .join(",") ?? "";
  useEffect(() => {
    const asyncFun = async () => {
      if (!loyaltyBrand?.ticker || constantTiers) {
        return;
      }
      const request = new GetLoyaltyTiersByBrandRequest();
      request.setBrandTicker(loyaltyBrand.ticker);
      const response = await client.getLoyaltyTiersByBrand(request);
      if (!response) {
        return;
      }
      setConstantTiers(
        response
          .getTiersList()
          .filter(it => it.hasConstantTier())
          .map(it => it.getConstantTier())
          .filter(it => it)
          .map(it => it?.toObject() as LoyaltyConstantTier.AsObject)
      );
    };
    asyncFun();
  }, [client, constantTiers, loyaltyBrand]);

  if (!chainData || !loyaltyBrand || !allowedLocales || !constantTiers) {
    return <div>Loading...</div>;
  }
  return (
    <LoyaltyLinksGenerator
      chainData={chainData}
      allowedLocales={allowedLocales}
      loyaltyBrand={loyaltyBrand}
      propertyTicker={propertyTicker}
      constantTiers={constantTiers}
      gtmCodes={gtmCodes}
    />
  );
}
export default React.memo(LoyaltyLinksGeneratorWrapper);
